import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useTheme } from '../../../../../../context/ThemeContext.jsx';
import DiscountCard from './DiscountCard';
import Spinner from './Spinner';

const PAGE_SIZE = 4;

const OffersModal = ({
    open,
    onClose,
    discounts,
    selectedDiscountIds,
    onToggle,
    onApplySelected,
    isApplying,
    formatMoney,
    isGuestUser = false
}) => {
    const { activeMode } = useTheme() || {};
    const [page, setPage] = useState(0);
    const totalPages = Math.ceil(discounts.length / PAGE_SIZE);
    const pagedDiscounts = discounts.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);
    const isDarkMode = activeMode === 'dark';
    const surface = isDarkMode ? 'var(--flex-surface)' : '#fff';
    const surfaceSoft = isDarkMode ? 'var(--flex-surface-soft)' : '#f8fafc';
    const primarySoft = isDarkMode ? 'var(--flex-primary-soft)' : 'var(--flex-primary-soft, #eff6ff)';
    const borderColor = isDarkMode ? 'var(--flex-border)' : '#e2e8f0';
    const borderSoft = isDarkMode ? 'var(--flex-border)' : '#f0f4f8';
    const bodyTextColor = isDarkMode ? 'var(--flex-text-muted)' : '#475569';
    const primaryTextColor = isDarkMode ? 'var(--flex-primary)' : 'var(--flex-primary-hover, #1d4ed8)';
    const primaryBorder = isDarkMode ? 'var(--flex-primary-muted)' : 'var(--flex-primary-muted, #bfdbfe)';

    useEffect(() => { if (open) setPage(0); }, [open]);

    const selectedCount = selectedDiscountIds.size;
    const allSelected = discounts.length > 0 && selectedCount === discounts.length;

    const handleSelectAll = () => {
        if (onToggle) onToggle();
    };

    return (
        <AnimatePresence>
            {open && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        style={{
                            position: 'fixed', inset: 0, zIndex: 50,
                            background: 'rgba(10, 18, 40, 0.55)',
                            backdropFilter: 'blur(5px)',
                        }}
                    />

                    {/* Modal */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 20 }}
                        transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
                        style={{
                            position: 'fixed', inset: 0, zIndex: 50,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            padding: 20, pointerEvents: 'none',
                        }}
                    >
                        <div style={{
                            pointerEvents: 'all',
                            width: '100%', maxWidth: 540,
                            maxHeight: '90vh',
                            background: surface,
                            borderRadius: 20,
                            boxShadow: '0 24px 64px rgba(0,0,0,0.14), 0 0 0 1px rgba(0,0,0,0.04)',
                            display: 'flex', flexDirection: 'column',
                            overflow: 'hidden',
                        }}>

                            {/* ── HEADER ── */}
                            <div style={{
                                padding: '16px 18px',
                                borderBottom: `1px solid ${borderSoft}`,
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                flexShrink: 0,
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <div style={{
                                        width: 36, height: 36, borderRadius: 10,
                                        background: primarySoft,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}>
                                        <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="var(--flex-primary, #2563eb)" strokeWidth={2.2}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <div style={{ fontSize: 14, fontWeight: 700, color: isDarkMode ? 'var(--flex-text)' : '#0f172a' }}>Available Offers</div>
                                        <div style={{ fontSize: 11, color: isDarkMode ? 'var(--flex-text-subtle)' : '#94a3b8', marginTop: 2 }}>
                                            {selectedCount > 0 && (
                                                <span style={{ color: 'var(--flex-primary, #2563eb)', fontWeight: 600 }}>{selectedCount} selected · </span>
                                            )}
                                            {discounts.length} offer{discounts.length !== 1 ? 's' : ''} available
                                        </div>
                                    </div>
                                </div>

                                <button
                                    onClick={onClose}
                                    style={{
                                        width: 28, height: 28, borderRadius: 8,
                                        background: surfaceSoft, border: `1px solid ${borderColor}`,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        cursor: 'pointer', flexShrink: 0,
                                    }}
                                >
                                    <svg width="11" height="11" fill="none" viewBox="0 0 24 24" stroke="#64748b" strokeWidth={2.5}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>

                            {/* ── SELECT ALL ── */}
                            {discounts.length > 0 && (
                                <div style={{ padding: '12px 18px 0', flexShrink: 0, display: 'grid', gap: 10 }}>
                                    <div style={{
                                        padding: '10px 12px',
                                        borderRadius: 10,
                                        border: `1px solid ${primaryBorder}`,
                                        background: primarySoft,
                                        fontSize: 12,
                                        color: primaryTextColor,
                                        fontWeight: 500,
                                    }}>
                                        Offers are applied together for this booking. Individual offer selection is not available.
                                    </div>
                                    <button
                                        onClick={handleSelectAll}
                                        disabled={isApplying}
                                        className="min-h-[44px] lg:min-h-0"
                                        style={{
                                            width: '100%', padding: '9px 16px',
                                            borderRadius: 10, fontWeight: 600, fontSize: 12,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                                            cursor: isApplying ? 'not-allowed' : 'pointer',
                                            transition: 'all 0.14s',
                                            border: allSelected ? '1.5px solid #fca5a5' : '1.5px solid var(--flex-accent, #93c5fd)',
                                            background: allSelected ? 'rgba(248, 113, 113, 0.12)' : primarySoft,
                                            color: allSelected ? '#f87171' : primaryTextColor,
                                        }}
                                    >
                                        {allSelected ? (
                                            <>
                                                <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                                Remove All Offers
                                            </>
                                        ) : (
                                            <>
                                                <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                                </svg>
                                                Select All Offers
                                            </>
                                        )}
                                    </button>
                                </div>
                            )}

                            {/* ── SELECTED BANNER ── */}
                            <AnimatePresence>
                                {selectedCount > 0 && (
                                    <motion.div
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: 'auto' }}
                                        exit={{ opacity: 0, height: 0 }}
                                        style={{ overflow: 'hidden', flexShrink: 0 }}
                                    >
                                        <div style={{ padding: '8px 18px 0' }}>
                                            <div style={{
                                                padding: '8px 12px', background: primarySoft,
                                                borderRadius: 8, border: `1px solid ${primaryBorder}`,
                                                display: 'flex', alignItems: 'center', gap: 6,
                                            }}>
                                                <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="var(--flex-primary, #2563eb)" strokeWidth={2.5}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                                <span style={{ fontSize: 12, fontWeight: 600, color: primaryTextColor }}>
                                                    {selectedCount} offer{selectedCount !== 1 ? 's' : ''}{' '}
                                                    {isGuestUser ? 'selected together for this booking' : 'selected together and ready to apply'}
                                                </span>
                                            </div>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            {/* ── CARDS GRID ── */}
                            <div style={{ padding: '12px 18px', overflowY: 'auto', flex: 1 }}>
                                {discounts.length === 0 ? (
                                    <div style={{ textAlign: 'center', padding: '36px 0' }}>
                                        <div style={{
                                            width: 52, height: 52, borderRadius: '50%',
                                            background: primarySoft,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            margin: '0 auto 10px',
                                        }}>
                                            <svg width="22" height="22" fill="none" viewBox="0 0 24 24" stroke="var(--flex-accent, #93c5fd)" strokeWidth={1.8}>
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                                            </svg>
                                        </div>
                                        <p style={{ color: bodyTextColor, fontWeight: 600, fontSize: 14 }}>No offers available</p>
                                        <p style={{ color: isDarkMode ? 'var(--flex-text-subtle)' : '#94a3b8', fontSize: 12, marginTop: 3 }}>Check back later for promotions</p>
                                    </div>
                                ) : (
                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                                        gap: 10,
                                        alignItems: 'start',
                                    }}>
                                        {pagedDiscounts.map((discount, i) => {
                                            const isSelected = selectedDiscountIds.has(discount.primaryId);
                                            return (
                                                <motion.div
                                                    key={discount.primaryId}
                                                    initial={{ opacity: 0, y: 6 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ delay: i * 0.04 }}
                                                >
                                                    <DiscountCard
                                                        discount={discount}
                                                        isSelected={isSelected}
                                                        formatMoney={formatMoney}
                                                        disabled={isApplying}
                                                    />
                                                </motion.div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            {/* ── PAGINATION ── */}
                            {totalPages > 1 && (
                                <div style={{
                                    padding: '10px 18px',
                                    borderTop: `1px solid ${borderSoft}`,
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    flexShrink: 0,
                                }}>
                                    <span style={{ fontSize: 11, color: isDarkMode ? 'var(--flex-text-subtle)' : '#94a3b8', fontWeight: 500 }}>
                                        Page {page + 1} of {totalPages}
                                    </span>
                                    <div style={{ display: 'flex', gap: 4 }}>
                                        <button
                                            onClick={() => setPage(p => Math.max(0, p - 1))}
                                            disabled={page === 0}
                                            style={{ width: 26, height: 26, borderRadius: 7, border: `1px solid ${borderColor}`, background: surface, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: page === 0 ? 'not-allowed' : 'pointer', opacity: page === 0 ? 0.3 : 1 }}
                                        >
                                            <svg width="11" height="11" fill="none" viewBox="0 0 24 24" stroke="#475569" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
                                        </button>
                                        {Array.from({ length: Math.min(3, totalPages) }).map((_, i) => {
                                            let pageNum;
                                            if (totalPages <= 3) pageNum = i;
                                            else if (page === 0) pageNum = i;
                                            else if (page === totalPages - 1) pageNum = totalPages - 3 + i;
                                            else pageNum = page - 1 + i;
                                            const isActive = page === pageNum;
                                            return (
                                                <button
                                                    key={pageNum}
                                                    onClick={() => setPage(pageNum)}
                                                    style={{ width: 26, height: 26, borderRadius: 7, fontSize: 11, fontWeight: 700, cursor: 'pointer', background: isActive ? 'var(--flex-primary, #2563eb)' : surface, border: isActive ? 'none' : `1px solid ${borderColor}`, color: isActive ? '#fff' : bodyTextColor, boxShadow: isActive ? '0 2px 6px var(--flex-primary-muted, rgba(37,99,235,0.28))' : 'none' }}
                                                >
                                                    {pageNum + 1}
                                                </button>
                                            );
                                        })}
                                        <button
                                            onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                                            disabled={page === totalPages - 1}
                                            style={{ width: 26, height: 26, borderRadius: 7, border: `1px solid ${borderColor}`, background: surface, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: page === totalPages - 1 ? 'not-allowed' : 'pointer', opacity: page === totalPages - 1 ? 0.3 : 1 }}
                                        >
                                            <svg width="11" height="11" fill="none" viewBox="0 0 24 24" stroke="#475569" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* ── FOOTER ── */}
                            <div style={{
                                padding: '12px 18px',
                                borderTop: `1px solid ${borderSoft}`,
                                flexShrink: 0,
                                display: 'flex',
                                gap: 8,
                                alignItems: 'center',
                            }}>
                                <button
                                    onClick={onClose}
                                    disabled={isApplying}
                                    className="min-h-[44px] lg:min-h-0"
                                    style={{
                                        flexShrink: 0,
                                        width: 90,
                                        padding: '10px 0',
                                        borderRadius: 9,
                                        border: `1px solid ${borderColor}`,
                                        background: surfaceSoft,
                                        color: bodyTextColor,
                                        fontWeight: 600,
                                        fontSize: 13,
                                        cursor: 'pointer',
                                    }}
                                >
                                    Cancel
                                </button>

                                <motion.button
                                    whileHover={{ scale: selectedCount > 0 && !isApplying ? 1.015 : 1 }}
                                    whileTap={{ scale: selectedCount > 0 && !isApplying ? 0.975 : 1 }}
                                    onClick={onApplySelected}
                                    disabled={isApplying || selectedCount === 0}
                                    className="min-h-[44px] lg:min-h-0"
                                    style={{
                                        flex: 1,
                                        padding: '10px 0',
                                        borderRadius: 9,
                                        border: 'none',
                                        background: selectedCount === 0
                                            ? '#cbd5e1'
                                            : 'linear-gradient(135deg, var(--flex-primary, #3b82f6) 0%, var(--flex-primary-hover, #1d4ed8) 100%)',
                                        color: '#fff',
                                        fontWeight: 700,
                                        fontSize: 13,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                                        cursor: isApplying || selectedCount === 0 ? 'not-allowed' : 'pointer',
                                        boxShadow: selectedCount > 0 ? '0 4px 14px var(--flex-primary-muted, rgba(29,78,216,0.28))' : 'none',
                                        transition: 'background 0.15s, box-shadow 0.15s',
                                    }}
                                >
                                    {isApplying ? (
                                        <><Spinner size={4} /> Applying…</>
                                    ) : (
                                        <>
                                            <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.8}>
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                            </svg>
                                            Apply All Offers
                                        </>
                                    )}
                                </motion.button>
                            </div>

                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};

export default OffersModal;
